import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

import axiosAdmin from '../../services/axiosAdmin';
import { displayErrors, formataMoeda } from '../../util/diversos';

import '../empresa/estamparia/dashboard/dashboardEstamparia.css';
import './dashboardEmpresa.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

/*
  Dashboard da tela inicial para empresas que não são estamparia.
  - Ordens de serviço: quando a empresa tem OS de aparelho abertas nos últimos 30 dias (tblosaparelho)
  - Geral: vendas (NFC-e e NF-e de saída), contas a receber/pagar e orçamentos
  Quando há OS recente, o usuário pode alternar entre os dois.
*/

const PERIODOS = [
  { id: '7d', label: '7 dias' },
  { id: '30d', label: '30 dias' },
  { id: 'mes', label: 'Este mês' },
  { id: 'mesAnterior', label: 'Mês anterior' },
  { id: 'ano', label: 'Este ano' },
];

const SITUACAO_OS = {
  ABERTA: { label: 'Abertas', icon: 'pi-inbox', classe: 'is-aberto' },
  FECHADA: { label: 'Fechadas (entregues)', icon: 'pi-check-circle', classe: 'is-fechado' },
  PAGA: { label: 'Pagas', icon: 'pi-dollar', classe: 'is-fechado' },
  CANCELADA: { label: 'Canceladas', icon: 'pi-times-circle', classe: 'is-cancelado' },
};

const TIPO_OS = {
  SERVICO: 'Serviço',
  GARANTIA: 'Garantia',
  ORCAMENTO: 'Orçamento',
  GRAVACAO: 'Gravação',
};

const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function calcularPeriodo(id) {
  const hoje = moment();
  switch (id) {
    case '7d': return [hoje.clone().subtract(6, 'days'), hoje];
    case '30d': return [hoje.clone().subtract(29, 'days'), hoje];
    case 'mesAnterior': {
      const inicio = hoje.clone().subtract(1, 'month').startOf('month');
      return [inicio, inicio.clone().endOf('month')];
    }
    case 'ano': return [hoje.clone().startOf('year'), hoje];
    default: return [hoje.clone().startOf('month'), hoje];
  }
}

// LocalDate pode vir como array [aaaa, m, d] ou como texto "aaaa-mm-dd"
function paraMoment(data) {
  if (!data) return null;
  if (Array.isArray(data)) return moment([data[0], data[1] - 1, data[2]]);
  return moment(String(data).substring(0, 10), 'YYYY-MM-DD');
}

function moeda(valor) {
  return formataMoeda(Number(valor) || 0);
}

function moedaCurta(valor) {
  const v = Number(valor) || 0;
  if (Math.abs(v) >= 1000000) return `R$ ${(v / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (Math.abs(v) >= 10000) return `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  return moeda(v);
}

function numero(valor) {
  return (Number(valor) || 0).toLocaleString('pt-BR');
}

function variacao(atual, anterior) {
  const a = Number(atual) || 0;
  const b = Number(anterior) || 0;
  if (b <= 0) return null;
  const perc = ((a / b) - 1) * 100;
  return `${perc >= 0 ? '+' : ''}${perc.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% vs. período anterior`;
}

function lerCor(variavel, padrao) {
  try {
    const cor = getComputedStyle(document.documentElement).getPropertyValue(variavel).trim();
    return cor || padrao;
  } catch (e) {
    return padrao;
  }
}

// Agrupa a série por mês quando o período é longo
function agruparSerie(lista, campos) {
  const agruparMes = lista.length > 62;
  const mapa = new Map();
  lista.forEach((item) => {
    const dia = paraMoment(item.data);
    if (!dia) return;
    const chave = agruparMes ? dia.format('YYYY-MM') : dia.format('YYYY-MM-DD');
    const rotulo = agruparMes ? `${MESES[dia.month()]}/${dia.format('YY')}` : dia.format('DD/MM');
    const atual = mapa.get(chave) || campos.reduce((obj, c) => ({ ...obj, [c]: 0 }), { rotulo });
    campos.forEach((c) => { atual[c] += Number(item[c]) || 0; });
    mapa.set(chave, atual);
  });
  return { agruparMes, pontos: Array.from(mapa.values()) };
}

function opcoesGrafico(serie, empilhado, legenda, linhasTooltip) {
  const corTexto = '#6b7a86';
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 250 },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: legenda
        ? { display: true, position: 'bottom', labels: { boxWidth: 12, boxHeight: 12, color: corTexto, font: { size: 11 } } }
        : { display: false },
      tooltip: {
        backgroundColor: '#1d2a33',
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (itens) => (serie.agruparMes ? itens[0].label : `Dia ${itens[0].label}`),
          label: () => null,
          afterBody: (itens) => linhasTooltip(serie.pontos[itens[0].dataIndex]),
        },
      },
    },
    scales: {
      x: {
        stacked: empilhado,
        grid: { display: false },
        ticks: { color: corTexto, maxRotation: 0, autoSkipPadding: 12, font: { size: 11 } },
      },
      y: {
        stacked: empilhado,
        beginAtZero: true,
        grid: { color: '#eef2f4' },
        ticks: { color: corTexto, font: { size: 11 }, maxTicksLimit: 5, callback: (v) => moedaCurta(v) },
      },
    },
  };
}

const ESTILO_BARRA = {
  borderSkipped: 'bottom',
  maxBarThickness: 28,
  categoryPercentage: 0.8,
  barPercentage: 0.9,
};

// ---------------------------------------------------------------------------
// Componentes simples reaproveitados pelos dois painéis
// ---------------------------------------------------------------------------

// eslint-disable-next-line react/prop-types
function Barras({ itens, rotulo, valor, formatar, vazio, dinheiro }) {
  const lista = itens || [];
  const maior = Math.max(1, ...lista.map(i => Number(valor(i)) || 0));
  if (lista.length === 0 || lista.every(i => !(Number(valor(i)) > 0))) {
    return <p className="dash-empty">{vazio}</p>;
  }
  return (
    <ul className={`dash-bars ${dinheiro ? 'is-money' : ''}`}>
      {lista.map(i => (
        <li key={rotulo(i)} title={`${rotulo(i)}: ${formatar(valor(i), i)}`}>
          <span className="dash-bar-label">{rotulo(i)}</span>
          <span className="dash-bar-track">
            <span className="dash-bar-fill" style={{ width: `${((Number(valor(i)) || 0) / maior) * 100}%` }} />
          </span>
          <span className="dash-bar-value">{dinheiro ? moedaCurta(valor(i)) : numero(valor(i))}</span>
        </li>
      ))}
    </ul>
  );
}

// eslint-disable-next-line react/prop-types
function Cartao({ titulo, icone, link, onLink, children, classe }) {
  return (
    <section className={`dash-card ${classe || ''}`}>
      <header className="dash-card-header">
        <h2><i className={`pi ${icone}`} />{titulo}</h2>
        {link ? <button type="button" className="dash-link" onClick={onLink}>{link}</button> : null}
      </header>
      {children}
    </section>
  );
}

// eslint-disable-next-line react/prop-types
function DataLista({ data, atraso }) {
  const m = paraMoment(data);
  if (!m) return <span className="dash-date">--</span>;
  const dias = moment().startOf('day').diff(m.clone().startOf('day'), 'days');
  return (
    <span className={`dash-date ${atraso ? 'is-late' : ''}`}>
      {m.format('DD/MM')}
      {atraso && dias > 0 ? <em>{dias} {dias === 1 ? 'dia' : 'dias'}</em> : null}
      {!atraso ? <em>{dias === 0 ? 'hoje' : DIAS_SEMANA[m.day()]}</em> : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Painel: ordens de serviço
// ---------------------------------------------------------------------------
// eslint-disable-next-line react/prop-types
function PainelOS({ os, tema, navigate }) {
  const irOS = () => navigate('/consultaordemServicoAparelho');

  const serie = useMemo(
    () => agruparSerie(get(os, 'serieDiaria', []) || [], ['faturadoValor', 'entregues', 'entradas']),
    [os],
  );

  const grafico = useMemo(() => {
    const cor = lerCor('--brand-600', '#2d7a71');
    return {
      data: {
        labels: serie.pontos.map(p => p.rotulo),
        datasets: [{
          label: 'Faturado',
          data: serie.pontos.map(p => Math.round(p.faturadoValor * 100) / 100),
          backgroundColor: cor,
          hoverBackgroundColor: lerCor('--brand-800', cor),
          borderRadius: { topLeft: 4, topRight: 4 },
          ...ESTILO_BARRA,
        }],
      },
      options: opcoesGrafico(serie, false, false, (p) => [
        `Faturado: ${moeda(p.faturadoValor)}`,
        `Entregues: ${numero(p.entregues)}`,
        `Entradas: ${numero(p.entradas)}`,
      ]),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serie, tema]);

  const entregues = Number(os.entreguesQuantidade) || 0;
  const ticketMedio = entregues > 0 ? (Number(os.faturadoValor) || 0) / entregues : 0;
  const totalSituacoes = (os.porSituacao || []).reduce((s, i) => s + (Number(i.quantidade) || 0), 0);
  const comparacao = variacao(os.faturadoValor, os.faturadoPeriodoAnteriorValor);

  const listaOS = (itens, atraso, vazio) => (
    (itens || []).length === 0 ? (
      <p className="dash-empty">{vazio}</p>
    ) : (
      <ul className="dash-list">
        {itens.map(o => (
          <li key={o.id}>
            <div className="dash-list-main">
              <strong>{o.numero ? `OS ${o.numero}` : 'OS'}{o.cliente ? ` · ${o.cliente}` : ''}</strong>
              <span>
                {o.aparelho || 'Aparelho não informado'}
                {o.tecnico ? ` · ${o.tecnico}` : ''}
              </span>
            </div>
            <div className="dash-list-side">
              <DataLista data={o.dataPrevisaoEntrega} atraso={atraso} />
              <span className="dash-value">{Number(o.valor) > 0 ? moeda(o.valor) : (TIPO_OS[o.tipo] || '')}</span>
            </div>
          </li>
        ))}
      </ul>
    )
  );

  return (
    <>
      <section className="dash-kpis">
        <button type="button" className="dash-kpi" onClick={irOS}>
          <span className="dash-kpi-label"><i className="pi pi-dollar" />Faturado</span>
          <strong>{moedaCurta(os.faturadoValor)}</strong>
          <span className="dash-kpi-sub">{numero(entregues)} OS entregues{comparacao ? ` · ${comparacao}` : ''}</span>
        </button>
        <button type="button" className="dash-kpi" onClick={irOS}>
          <span className="dash-kpi-label"><i className="pi pi-sign-in" />Entradas</span>
          <strong>{numero(os.entradasQuantidade)}</strong>
          <span className="dash-kpi-sub">
            {numero(os.orcamentosQuantidade)} orçamentos · {numero(os.garantiasQuantidade)} garantias
            {Number(os.canceladasQuantidade) > 0 ? ` · ${numero(os.canceladasQuantidade)} canceladas` : ''}
          </span>
        </button>
        <button type="button" className="dash-kpi" onClick={irOS}>
          <span className="dash-kpi-label"><i className="pi pi-chart-line" />Ticket médio</span>
          <strong>{moedaCurta(ticketMedio)}</strong>
          <span className="dash-kpi-sub">por OS entregue no período</span>
        </button>
        <button type="button" className="dash-kpi" onClick={irOS}>
          <span className="dash-kpi-label"><i className="pi pi-cog" />Em aberto</span>
          <strong>{numero(os.abertasQuantidade)}</strong>
          <span className="dash-kpi-sub">OS abertas (últimos 90 dias) · {moedaCurta(os.abertasValorOrcado)}</span>
        </button>
        <button type="button" className={`dash-kpi ${os.atrasadasQuantidade > 0 ? 'is-alert' : ''}`} onClick={irOS}>
          <span className="dash-kpi-label">
            <i className={`pi ${os.atrasadasQuantidade > 0 ? 'pi-exclamation-triangle' : 'pi-calendar'}`} />
            Entregas atrasadas
          </span>
          <strong>{numero(os.atrasadasQuantidade)}</strong>
          <span className="dash-kpi-sub">{numero(os.previstasProximasQuantidade)} previstas nos próximos 7 dias</span>
        </button>
      </section>

      <div className="dash-grid">
        <Cartao titulo={`Faturamento por ${serie.agruparMes ? 'mês' : 'dia'}`} icone="pi-chart-bar" classe="dash-chart-card">
          <div className="dash-chart">
            {serie.pontos.some(p => p.faturadoValor > 0) ? (
              <Bar data={grafico.data} options={grafico.options} />
            ) : (
              <p className="dash-empty">Nenhuma OS entregue no período.</p>
            )}
          </div>
        </Cartao>

        <Cartao titulo="OS por situação" icone="pi-list" link="Ver OS" onLink={irOS}>
          {totalSituacoes === 0 ? (
            <p className="dash-empty">Nenhuma OS aberta no período.</p>
          ) : (
            <ul className="dash-status">
              {(os.porSituacao || []).map((s) => {
                const info = SITUACAO_OS[s.chave] || { label: s.chave, icon: 'pi-circle', classe: '' };
                const perc = totalSituacoes > 0 ? Math.round((Number(s.quantidade) / totalSituacoes) * 100) : 0;
                return (
                  <li key={s.chave} className={info.classe}>
                    <i className={`pi ${info.icon}`} />
                    <span>{info.label}</span>
                    <strong>{numero(s.quantidade)}</strong>
                    <em>{perc}%</em>
                  </li>
                );
              })}
            </ul>
          )}
        </Cartao>

        <Cartao titulo="Faturado por técnico" icone="pi-users">
          <Barras
            itens={os.tecnicos}
            rotulo={t => t.nome}
            valor={t => t.valor}
            formatar={(v, t) => `${moeda(v)} em ${numero(t.quantidade)} OS`}
            vazio="Nenhuma OS entregue no período."
            dinheiro
          />
        </Cartao>

        <Cartao titulo="Entregas atrasadas" icone="pi-exclamation-circle" link="Ver OS" onLink={irOS}>
          {listaOS(os.atrasadas, true, 'Nenhuma entrega atrasada.')}
        </Cartao>

        <Cartao titulo="Próximas entregas (7 dias)" icone="pi-calendar" link="Ver OS" onLink={irOS}>
          {listaOS(os.proximas, false, 'Nenhuma entrega prevista para os próximos 7 dias.')}
        </Cartao>

        <Cartao titulo="Marcas mais atendidas" icone="pi-mobile">
          <Barras
            itens={os.marcas}
            rotulo={m => m.chave}
            valor={m => m.quantidade}
            formatar={v => `${numero(v)} OS`}
            vazio="Nenhuma marca informada no período."
          />
        </Cartao>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Painel: geral (vendas, financeiro e orçamentos)
// ---------------------------------------------------------------------------
// eslint-disable-next-line react/prop-types
function PainelGeral({ geral, tema, navigate }) {
  const serie = useMemo(
    () => agruparSerie(get(geral, 'serieDiaria', []) || [], ['nfceValor', 'nfeValor', 'quantidade']),
    [geral],
  );

  const grafico = useMemo(() => {
    const cor = lerCor('--brand-600', '#2d7a71');
    const cor2 = lerCor('--brand-900', '#16403b');
    return {
      data: {
        labels: serie.pontos.map(p => p.rotulo),
        datasets: [
          {
            label: 'NFC-e',
            data: serie.pontos.map(p => Math.round(p.nfceValor * 100) / 100),
            backgroundColor: cor,
            ...ESTILO_BARRA,
          },
          {
            label: 'NF-e',
            data: serie.pontos.map(p => Math.round(p.nfeValor * 100) / 100),
            backgroundColor: cor2,
            borderRadius: { topLeft: 4, topRight: 4 },
            ...ESTILO_BARRA,
          },
        ],
      },
      options: opcoesGrafico(serie, true, Number(geral.nfeQuantidade) > 0 && Number(geral.nfceQuantidade) > 0, (p) => [
        `Total: ${moeda(p.nfceValor + p.nfeValor)} (${numero(p.quantidade)} notas)`,
        `NFC-e: ${moeda(p.nfceValor)}`,
        `NF-e: ${moeda(p.nfeValor)}`,
      ]),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serie, tema]);

  const receber = geral.receber || {};
  const pagar = geral.pagar || {};
  const comparacao = variacao(geral.vendasValor, geral.vendasPeriodoAnteriorValor);
  const qtdVendas = Number(geral.vendasQuantidade) || 0;
  const irFinanceiro = () => navigate('/painelfinanceiro');

  const resumoFinanceiro = (f, tipo) => (
    <ul className="dash-fin">
      <li className={Number(f.vencidoValor) > 0 ? 'is-late' : ''}>
        <span>Vencidas</span>
        <strong>{moeda(f.vencidoValor)}</strong>
        <em>{numero(f.vencidoQuantidade)}</em>
      </li>
      <li>
        <span>Vencem hoje</span>
        <strong>{moeda(f.hojeValor)}</strong>
        <em>{numero(f.hojeQuantidade)}</em>
      </li>
      <li>
        <span>Próximos 7 dias</span>
        <strong>{moeda(f.proximos7Valor)}</strong>
        <em>{numero(f.proximos7Quantidade)}</em>
      </li>
      <li className="is-total">
        <span>Total a {tipo}</span>
        <strong>{moeda(f.abertoValor)}</strong>
        <em>{numero(f.abertoQuantidade)}</em>
      </li>
    </ul>
  );

  const listaContas = (itens, atraso, vazio) => (
    (itens || []).length === 0 ? (
      <p className="dash-empty">{vazio}</p>
    ) : (
      <ul className="dash-list">
        {itens.map(c => (
          <li key={c.id}>
            <div className="dash-list-main">
              <strong>{c.participante || 'Participante não informado'}</strong>
              <span>{c.documento ? `Documento ${c.documento}` : 'Sem número de documento'}</span>
            </div>
            <div className="dash-list-side">
              <DataLista data={c.vencimento} atraso={atraso} />
              <span className="dash-value">{moeda(c.valor)}</span>
            </div>
          </li>
        ))}
      </ul>
    )
  );

  const documentos = [
    { nome: 'NFC-e', quantidade: geral.nfceQuantidade, valor: geral.nfceValor },
    { nome: 'NF-e', quantidade: geral.nfeQuantidade, valor: geral.nfeValor },
  ];

  return (
    <>
      <section className="dash-kpis">
        <button type="button" className="dash-kpi" onClick={() => navigate('/consultanfce')}>
          <span className="dash-kpi-label"><i className="pi pi-shopping-cart" />Vendas</span>
          <strong>{moedaCurta(geral.vendasValor)}</strong>
          <span className="dash-kpi-sub">
            {numero(qtdVendas)} notas · ticket médio {moeda(qtdVendas > 0 ? (Number(geral.vendasValor) || 0) / qtdVendas : 0)}
            {comparacao ? ` · ${comparacao}` : ''}
          </span>
        </button>
        <button type="button" className="dash-kpi" onClick={() => navigate('/consultanfce')}>
          <span className="dash-kpi-label"><i className="pi pi-calendar" />Vendas hoje</span>
          <strong>{moedaCurta(geral.vendasHojeValor)}</strong>
          <span className="dash-kpi-sub">{numero(geral.vendasHojeQuantidade)} notas emitidas hoje</span>
        </button>
        <button type="button" className={`dash-kpi ${Number(receber.vencidoValor) > 0 ? 'is-alert' : ''}`} onClick={irFinanceiro}>
          <span className="dash-kpi-label"><i className="pi pi-wallet" />A receber</span>
          <strong>{moedaCurta(receber.abertoValor)}</strong>
          <span className="dash-kpi-sub">
            {Number(receber.vencidoValor) > 0
              ? `${moedaCurta(receber.vencidoValor)} vencidos (${numero(receber.vencidoQuantidade)})`
              : 'nenhuma conta vencida'}
          </span>
        </button>
        <button type="button" className="dash-kpi" onClick={irFinanceiro}>
          <span className="dash-kpi-label"><i className="pi pi-credit-card" />A pagar hoje</span>
          <strong>{moedaCurta(pagar.hojeValor)}</strong>
          <span className="dash-kpi-sub">
            {moedaCurta(pagar.proximos7Valor)} nos próximos 7 dias
            {Number(pagar.vencidoValor) > 0 ? ` · ${moedaCurta(pagar.vencidoValor)} vencidos` : ''}
          </span>
        </button>
        <button type="button" className="dash-kpi" onClick={() => navigate('/consultaorcamento')}>
          <span className="dash-kpi-label"><i className="pi pi-file" />Orçamentos</span>
          <strong>{numero(geral.orcamentosQuantidade)}</strong>
          <span className="dash-kpi-sub">{moedaCurta(geral.orcamentosValor)} orçados no período</span>
        </button>
      </section>

      <div className="dash-grid">
        <Cartao titulo={`Vendas por ${serie.agruparMes ? 'mês' : 'dia'}`} icone="pi-chart-bar" classe="dash-chart-card">
          <div className="dash-chart">
            {serie.pontos.some(p => p.nfceValor + p.nfeValor > 0) ? (
              <Bar data={grafico.data} options={grafico.options} />
            ) : (
              <p className="dash-empty">Nenhuma venda no período.</p>
            )}
          </div>
        </Cartao>

        <Cartao titulo="Contas a receber" icone="pi-arrow-down-left" link="Ver" onLink={irFinanceiro}>
          {resumoFinanceiro(receber, 'receber')}
        </Cartao>

        <Cartao titulo="Contas a pagar" icone="pi-arrow-up-right" link="Ver" onLink={irFinanceiro}>
          {resumoFinanceiro(pagar, 'pagar')}
        </Cartao>

        <Cartao titulo="Recebimentos vencidos" icone="pi-exclamation-circle" link="Ver" onLink={irFinanceiro}>
          {listaContas(geral.receberVencidas, true, 'Nenhum recebimento vencido.')}
        </Cartao>

        <Cartao titulo="Pagamentos dos próximos 7 dias" icone="pi-calendar" link="Ver" onLink={irFinanceiro}>
          {listaContas(geral.pagarProximas, false, 'Nenhum pagamento previsto para os próximos 7 dias.')}
        </Cartao>

        <Cartao titulo="Vendas por documento" icone="pi-copy">
          <Barras
            itens={documentos}
            rotulo={d => d.nome}
            valor={d => d.valor}
            formatar={(v, d) => `${moeda(v)} em ${numero(d.quantidade)} notas`}
            vazio="Nenhuma venda no período."
            dinheiro
          />
        </Cartao>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
export default function DashboardEmpresa() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user) || {};

  const [periodo, setPeriodo] = useState('mes');
  const [modo, setModo] = useState('');
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [tema, setTema] = useState(document.documentElement.getAttribute('data-theme'));

  useEffect(() => {
    const observer = new MutationObserver(() => setTema(document.documentElement.getAttribute('data-theme')));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      try {
        setCarregando(true);
        setErro('');
        const [inicio, fim] = calcularPeriodo(periodo);
        const idUsuario = get(user, 'id', 0);
        let params = `/empresadashboard?idUsuario=${idUsuario}`
          + `&dataInicial=${inicio.format('DD-MM-YYYY')}&dataFinal=${fim.format('DD-MM-YYYY')}`;
        if (modo) params = `${params}&modo=${modo}`;
        const { data } = await axiosAdmin.get(params);
        if (ativo) setDados(data);
      } catch (err) {
        if (ativo) {
          const status = get(err, 'response.status', 0);
          const mensagemServidor = get(err, 'response.data.errors[0]', '') || get(err, 'response.data.message', '');
          let detalhe = '';
          if (!err.response) {
            detalhe = 'sem resposta do servidor (backend fora do ar ou endereço incorreto)';
          } else if (status === 404) {
            detalhe = 'serviço /empresadashboard não encontrado (backend desatualizado ou versão antiga ainda ativa no Tomcat)';
          } else if (status === 401 || status === 403) {
            detalhe = 'acesso negado';
          } else {
            detalhe = `erro ${status}${mensagemServidor ? `: ${mensagemServidor}` : ''}`;
          }
          setErro(`Não foi possível carregar os indicadores - ${detalhe}.`);
          if (status === 403) displayErrors(err, dispatch, navigate, '', '');
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    carregar();
    return () => { ativo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo, modo]);

  const [inicioPeriodo, fimPeriodo] = calcularPeriodo(periodo);
  const modoAtual = get(dados, 'modo', '');

  return (
    <div className="dash-estamparia dash-empresa">
      <div className="dash-top">
        <div>
          <span className="section-eyebrow">{modoAtual === 'OS' ? 'Ordens de serviço' : 'Visão geral'}</span>
          <h1>Olá{user.nome ? `, ${user.nome.split(' ')[0]}` : ''}!</h1>
          <p>
            {inicioPeriodo.format('DD/MM/YYYY')} a {fimPeriodo.format('DD/MM/YYYY')}
            {carregando ? <span className="dash-loading"><i className="pi pi-spin pi-spinner" /> atualizando</span> : null}
          </p>
        </div>
        <div className="dash-top-actions">
          {get(dados, 'possuiOsRecente', false) ? (
            <div className="dash-periods dash-modos" role="group" aria-label="Painel">
              <button
                type="button"
                className={modoAtual === 'OS' ? 'is-active' : ''}
                aria-pressed={modoAtual === 'OS'}
                onClick={() => setModo('OS')}
              >
                <i className="pi pi-cog" /> OS
              </button>
              <button
                type="button"
                className={modoAtual === 'GERAL' ? 'is-active' : ''}
                aria-pressed={modoAtual === 'GERAL'}
                onClick={() => setModo('GERAL')}
              >
                <i className="pi pi-th-large" /> Geral
              </button>
            </div>
          ) : null}
          <div className="dash-periods" role="group" aria-label="Período">
            {PERIODOS.map(p => (
              <button
                key={p.id}
                type="button"
                className={periodo === p.id ? 'is-active' : ''}
                aria-pressed={periodo === p.id}
                onClick={() => setPeriodo(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {erro && !dados ? (
        <div className="dash-card dash-error"><i className="pi pi-exclamation-triangle" /> {erro}</div>
      ) : null}

      {dados && modoAtual === 'OS' && dados.os ? <PainelOS os={dados.os} tema={tema} navigate={navigate} /> : null}
      {dados && modoAtual === 'GERAL' && dados.geral ? <PainelGeral geral={dados.geral} tema={tema} navigate={navigate} /> : null}

      {!dados && carregando ? (
        <div className="dash-kpis is-skeleton">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="dash-kpi" />)}
        </div>
      ) : null}
    </div>
  );
}
